/**
 * 🎨 MOBILE FILTER SHEET - Redesign 2026
 * 
 * Changes:
 * - REMOVED "Verfügbarkeit" section (Jetzt geöffnet + Mit Lieferung)
 *   → "Jetzt geöffnet" is in QuickFilterChips
 *   → "Lieferung/Abholung" is in DeliveryModeToggle (header)
 * - Kept: Ernährung, Küche, Entfernung
 * - Cleaner section spacing and lighter visual weight
 */

import React, { useEffect, useRef } from 'react';
import { X as CloseIcon, RotateCcw, MapPin, Check, Leaf } from 'lucide-react';
import { MobileSheet, MobileSheetHeader, MobileSheetContent, MobileSheetFooter } from '@/components/ui/mobile-sheet';
import { useFilterStore } from '@/lib/FilterStore';
import { Cuisine, Skill } from '@/lib/types';
import { cn } from '@/lib/utils';

interface MobileFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  restaurantCount: number;
  onResetFilters: () => void;
  cuisines?: Cuisine[];
  dietarySkills?: Skill[];
}

const SectionHeader: React.FC<{ 
  title: string; 
  icon?: React.ReactNode;
  badge?: number;
  className?: string;
}> = ({ title, icon, badge, className }) => (
  <div className={cn("flex items-center justify-between mb-4", className)}>
    <div className="flex items-center gap-2">
      {icon && (
        <div className="w-7 h-7 rounded-lg bg-[#009544]/8 flex items-center justify-center">
          {icon}
        </div>
      )}
      <h3 className="text-[14px] font-semibold text-gray-900 tracking-tight">
        {title}
      </h3>
    </div>
    {badge !== undefined && badge > 0 && (
      <span className="text-[11px] bg-[#009544]/10 text-[#009544] px-2 py-0.5 rounded-full font-bold tabular-nums">
        {badge}
      </span>
    )}
  </div>
);

export const MobileFilterSheet: React.FC<MobileFilterSheetProps> = ({
  open,
  onOpenChange,
  restaurantCount,
  onResetFilters,
  cuisines = [],
  dietarySkills = [],
}) => {
  const filterStore = useFilterStore();
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeFilterCount = filterStore.getActiveFiltersCount();
  const hasActiveFilters = activeFilterCount > 0;

  const safeCuisines = cuisines || [];
  const safeDietarySkills = dietarySkills || [];

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [open]);

  const toggleCuisine = (id: number) => {
    const current = [...filterStore.cuisineIds];
    if (current.includes(id)) {
      filterStore.setCuisines(current.filter(cid => cid !== id));
    } else {
      filterStore.setCuisines([...current, id]);
    }
  };

  const toggleSkill = (id: number) => {
    const current = [...filterStore.skills];
    if (current.includes(id)) {
      filterStore.setSkills(current.filter(sid => sid !== id));
    } else {
      filterStore.setSkills([...current, id]);
    }
  };

  return (
    <MobileSheet 
      open={open} 
      onOpenChange={onOpenChange}
      title="Filter"
    >
      {/* Header */}
      <MobileSheetHeader className="px-6 py-4">
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900 tracking-tight">
            Filter
          </h2>
          {hasActiveFilters && (
            <p className="text-[12px] text-[#009544] font-medium mt-0.5 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#009544]" />
              {activeFilterCount} aktiv
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={onResetFilters}
              className="h-10 px-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium text-sm transition-colors active:scale-95 flex items-center gap-1.5"
              aria-label="Alle Filter zurücksetzen"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reset</span>
            </button>
          )}

          <button
            onClick={() => onOpenChange(false)}
            className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors active:scale-95 flex items-center justify-center"
            aria-label="Schließen"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>
      </MobileSheetHeader>

      {/* Scrollable Content */}
      <MobileSheetContent>
        <div 
          ref={scrollRef}
          className="px-6 pt-5 pb-6 space-y-8"
          style={{ 
            paddingLeft: 'max(24px, env(safe-area-inset-left))',
            paddingRight: 'max(24px, env(safe-area-inset-right))'
          }}
        >
          {/* SECTION 1: ERNÄHRUNG */}
          {safeDietarySkills.length > 0 && (
            <section>
              <SectionHeader 
                title="Ernährung" 
                icon={<Leaf className="w-3.5 h-3.5 text-[#009544]" />}
                badge={filterStore.skills.length}
              />
              <div className="flex flex-wrap gap-2.5">
                {safeDietarySkills.map(skill => (
                  <button
                    key={skill.id}
                    onClick={() => toggleSkill(skill.id)}
                    className={cn(
                      "h-10 px-4 rounded-full font-medium text-[13px] transition-all duration-200 active:scale-95",
                      filterStore.skills.includes(skill.id)
                        ? "bg-[#009544] text-white shadow-sm"
                        : "bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#009544]/30"
                    )}
                  >
                    {filterStore.skills.includes(skill.id) && (
                      <Check className="w-3.5 h-3.5 mr-1 inline" />
                    )}
                    {skill.name}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* SECTION 2: KÜCHE */}
          {safeCuisines.length > 0 && (
            <section>
              <SectionHeader 
                title="Küche" 
                badge={filterStore.cuisineIds.length}
              />
              <div className="flex flex-wrap gap-2.5">
                {safeCuisines.map(cuisine => (
                  <button
                    key={cuisine.id}
                    onClick={() => toggleCuisine(cuisine.id)}
                    className={cn(
                      "h-10 px-4 rounded-full font-medium text-[13px] transition-all duration-200 active:scale-95",
                      filterStore.cuisineIds.includes(cuisine.id)
                        ? "bg-[#009544] text-white shadow-sm"
                        : "bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#009544]/30"
                    )}
                  >
                    {filterStore.cuisineIds.includes(cuisine.id) && (
                      <Check className="w-3.5 h-3.5 mr-1 inline" />
                    )}
                    {cuisine.name}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* SECTION 3: ENTFERNUNG */}
          {filterStore.location && (
            <section>
              <SectionHeader 
                title="Maximale Entfernung"
                icon={<MapPin className="w-3.5 h-3.5 text-[#009544]" />}
              />
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[13px] text-gray-500">Umkreis</span>
                  <span className="text-base font-bold text-[#009544] tabular-nums">
                    {filterStore.distance} km
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  step="1"
                  value={filterStore.distance}
                  onChange={(e) => filterStore.setDistance(Number(e.target.value))}
                  className="w-full h-2.5 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #009544 0%, #009544 ${(filterStore.distance / 50) * 100}%, #e5e7eb ${(filterStore.distance / 50) * 100}%, #e5e7eb 100%)`
                  }}
                />
                <div className="flex justify-between text-[11px] text-gray-400 mt-2 font-medium">
                  <span>1 km</span>
                  <span>25 km</span>
                  <span>50 km</span>
                </div>
              </div>
            </section>
          )}

          {!filterStore.location && (
            <section>
              <div className="py-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                <MapPin className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                <p className="text-[13px] font-medium text-gray-500">Standort wählen</p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  Um nach Entfernung zu filtern
                </p>
              </div>
            </section>
          )}

          <div className="h-6" aria-hidden="true" />
        </div>
      </MobileSheetContent>

      {/* Footer */}
      <MobileSheetFooter>
        <button
          onClick={() => onOpenChange(false)}
          className="w-full h-[52px] rounded-xl bg-[#009544] text-white font-semibold text-[15px] shadow-lg shadow-[#009544]/25 hover:bg-[#007538] transition-colors active:scale-[0.98] flex items-center justify-center gap-1.5"
        >
          <span className="tabular-nums">{restaurantCount}</span>
          <span>Restaurants anzeigen</span>
        </button>
      </MobileSheetFooter>
    </MobileSheet>
  );
};
